import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, FloppyDisk } from "@phosphor-icons/react";
import { AppHeader } from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

const slug = (s) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

const withDefaults = (t) => ({
  ...t,
  default_fields: t.default_fields || [],
  require_photo:
    t.require_photo === undefined || t.require_photo === null
      ? (t.label || "").toLowerCase() === "install"
      : t.require_photo,
});

export default function TaskTypesSettings() {
  const [types, setTypes] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/settings/task-types")
      .then(({ data }) => setTypes((data.task_types || []).map(withDefaults)))
      .catch(() => {});
  }, []);

  const add = () =>
    setTypes((t) => [
      ...t,
      { id: "", label: "", color: "#8b5cf6", default_fields: [], require_photo: false },
    ]);
  const upd = (i, k, v) =>
    setTypes((t) => t.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const remove = (i) => setTypes((t) => t.filter((_, j) => j !== i));

  const addField = (i) =>
    setTypes((t) =>
      t.map((x, j) =>
        j === i
          ? { ...x, default_fields: [...(x.default_fields || []), { label: "", value: "" }] }
          : x,
      ),
    );
  const updField = (i, k, key, val) =>
    setTypes((t) =>
      t.map((x, j) =>
        j === i
          ? {
              ...x,
              default_fields: (x.default_fields || []).map((f, m) =>
                m === k ? { ...f, [key]: val } : f,
              ),
            }
          : x,
      ),
    );
  const removeField = (i, k) =>
    setTypes((t) =>
      t.map((x, j) =>
        j === i
          ? {
              ...x,
              default_fields: (x.default_fields || []).filter((_, m) => m !== k),
            }
          : x,
      ),
    );

  const save = async () => {
    const clean = types
      .filter((t) => (t.label || "").trim())
      .map((t) => ({
        id: t.id || slug(t.label),
        label: t.label.trim(),
        color: t.color || "#71717a",
        require_photo: !!t.require_photo,
        default_fields: (t.default_fields || [])
          .filter((f) => (f.label || "").trim())
          .map((f) => ({ label: f.label.trim(), value: f.value || "" })),
      }));
    setSaving(true);
    try {
      const { data } = await api.put("/settings/task-types", {
        task_types: clean,
      });
      setTypes((data.task_types || clean).map(withDefaults));
      toast.success("Task types saved");
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Task Types" />
      <main className="relative z-10 mx-auto max-w-2xl pb-20 pt-6">
        <div className="text-xs uppercase tracking-widest text-zinc-600">
          Settings
        </div>
        <h1
          className="text-2xl font-bold tracking-tight sm:text-3xl"
          data-testid="task-types-title"
        >
          Task Types
        </h1>
        <p className="mt-1 text-sm text-zinc-500">
          The visit / task types your crew can schedule on jobs and projects
          (Templating, Fabrication, Install, Visit…). Pick a color so they stand
          out on the calendar, and give each type its own default fields — e.g.
          Install can always ask for a “Gate code”.
        </p>

        <div className="mt-6 space-y-3" data-testid="task-types-list">
          {types.map((t, i) => (
            <div
              key={i}
              data-testid={`task-type-row-${i}`}
              className="rounded-lg border border-zinc-800 bg-zinc-900 p-3"
            >
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  data-testid={`task-type-color-${i}`}
                  value={t.color || "#71717a"}
                  onChange={(e) => upd(i, "color", e.target.value)}
                  className="h-9 w-10 cursor-pointer rounded border border-zinc-800 bg-transparent"
                />
                <Input
                  data-testid={`task-type-label-${i}`}
                  value={t.label}
                  onChange={(e) => upd(i, "label", e.target.value)}
                  placeholder="Type name"
                  className="h-10 flex-1 border-zinc-800 bg-zinc-950"
                />
                <button
                  type="button"
                  data-testid={`task-type-remove-${i}`}
                  onClick={() => remove(i)}
                  className="text-zinc-500 transition-colors duration-150 hover:text-red-400"
                >
                  <Trash size={16} weight="bold" />
                </button>
              </div>

              <div className="mt-3 border-t border-zinc-800 pt-3 pl-1">
                <label
                  className="mb-3 flex cursor-pointer items-center gap-2 text-xs text-zinc-300"
                  data-testid={`task-type-require-photo-label-${i}`}
                >
                  <input
                    type="checkbox"
                    data-testid={`task-type-require-photo-${i}`}
                    checked={!!t.require_photo}
                    onChange={(e) => upd(i, "require_photo", e.target.checked)}
                    className="h-4 w-4 accent-indigo-500"
                  />
                  Show a "Photo optional" reminder badge on this task for crews
                </label>
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                    Default fields
                  </span>
                  <button
                    type="button"
                    data-testid={`task-type-field-add-${i}`}
                    onClick={() => addField(i)}
                    className="inline-flex items-center gap-1 text-xs text-indigo-300 hover:text-indigo-200"
                  >
                    <Plus size={12} weight="bold" /> Add field
                  </button>
                </div>
                {(t.default_fields || []).length === 0 ? (
                  <p className="text-xs text-zinc-600">
                    No default fields — new {t.label || "tasks"} start blank.
                  </p>
                ) : (
                  <div className="space-y-2" data-testid={`task-type-fields-${i}`}>
                    {(t.default_fields || []).map((f, k) => (
                      <div key={k} className="flex gap-2">
                        <Input
                          data-testid={`task-type-field-label-${i}-${k}`}
                          value={f.label}
                          onChange={(e) => updField(i, k, "label", e.target.value)}
                          placeholder="Field name (e.g. Gate code)"
                          className="h-9 flex-1 border-zinc-800 bg-zinc-950 text-sm"
                        />
                        <Input
                          data-testid={`task-type-field-value-${i}-${k}`}
                          value={f.value}
                          onChange={(e) => updField(i, k, "value", e.target.value)}
                          placeholder="Default value (optional)"
                          className="h-9 flex-1 border-zinc-800 bg-zinc-950 text-sm"
                        />
                        <button
                          type="button"
                          data-testid={`task-type-field-remove-${i}-${k}`}
                          onClick={() => removeField(i, k)}
                          className="text-zinc-500 hover:text-red-400"
                        >
                          <Trash size={14} weight="bold" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          {types.length === 0 && (
            <p className="rounded-lg border border-dashed border-zinc-800 p-6 text-center text-sm text-zinc-500">
              No task types yet — add your first one below.
            </p>
          )}
        </div>

        <div className="mt-4 flex gap-2">
          <Button
            type="button"
            variant="outline"
            data-testid="task-type-add"
            onClick={add}
            className="border-zinc-700 bg-transparent"
          >
            <Plus size={14} weight="bold" className="mr-1" /> Add type
          </Button>
          <Button
            type="button"
            data-testid="task-types-save"
            onClick={save}
            disabled={saving}
            className="bg-indigo-500 hover:bg-indigo-400"
          >
            <FloppyDisk size={14} weight="bold" className="mr-1" /> Save
          </Button>
        </div>
      </main>
    </div>
  );
}
